/**
 * Agent Identities list page component.
 *
 * @module pages/AgentIdentitiesPage
 */

import { useState } from 'react';
import { Link } from 'react-router';
import { Plus, UserCog, Trash2, UserPlus, UserMinus } from 'lucide-react';
import { LoadingPage, Badge, ConfirmModal } from '@/components/ui';
import {
  useAgentIdentities,
  useDeleteAgentIdentity,
  useUpdateAgentIdentity,
  agentIdentityKeys,
} from '@/hooks';
import { agentIdentityService } from '@/services';
import { useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import type { AgentIdentityResponse } from '@/types';

/**
 * Agent identities list page
 */
export function AgentIdentitiesPage() {
  const { data: agentIdentities, isLoading } = useAgentIdentities();
  const deleteMutation = useDeleteAgentIdentity();
  const updateMutation = useUpdateAgentIdentity();

  const [deleteTarget, setDeleteTarget] = useState<AgentIdentityResponse | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState({
    current: 0,
    total: 0,
    currentId: '',
  });
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Agent user modal state
  const [addAgentUserTarget, setAddAgentUserTarget] = useState<AgentIdentityResponse | null>(null);
  const [removeAgentUserTarget, setRemoveAgentUserTarget] = useState<AgentIdentityResponse | null>(
    null
  );
  const [userPrincipalName, setUserPrincipalName] = useState('');

  const queryClient = useQueryClient();

  const handleDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id, {
        onSuccess: () => setDeleteTarget(null),
      });
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && agentIdentities) {
      setSelectedIds(new Set(agentIdentities.map((identity) => identity.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    setIsBulkDeleting(true);
    const idsToDelete = Array.from(selectedIds);
    setBulkDeleteProgress({ current: 0, total: idsToDelete.length, currentId: '' });

    const BATCH_SIZE = 5;
    let completed = 0;

    for (let i = 0; i < idsToDelete.length; i += BATCH_SIZE) {
      const batch = idsToDelete.slice(i, i + BATCH_SIZE);

      await Promise.all(
        batch.map(async (id) => {
          try {
            await agentIdentityService.delete(id);
            completed++;
            setBulkDeleteProgress({
              current: completed,
              total: idsToDelete.length,
              currentId: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : id,
            });
          } catch (error) {
            console.error(`Failed to delete agent identity ${id}:`, error);
            completed++;
            setBulkDeleteProgress({
              current: completed,
              total: idsToDelete.length,
              currentId: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : id,
            });
          }
        })
      );
    }

    queryClient.invalidateQueries({ queryKey: agentIdentityKeys.lists() });

    setBulkDeleteProgress({
      current: idsToDelete.length,
      total: idsToDelete.length,
      currentId: '',
    });
    setIsBulkDeleting(false);
    setSelectedIds(new Set());

    setTimeout(() => {
      setShowBulkDeleteModal(false);
      setBulkDeleteProgress({ current: 0, total: 0, currentId: '' });
    }, 1000);
  };

  const handleAddAgentUser = () => {
    if (addAgentUserTarget && userPrincipalName.trim()) {
      updateMutation.mutate(
        {
          id: addAgentUserTarget.id,
          request: { agentUser: { userPrincipalName: userPrincipalName.trim() } },
        },
        {
          onSuccess: () => {
            setAddAgentUserTarget(null);
            setUserPrincipalName('');
          },
        }
      );
    }
  };

  const handleRemoveAgentUser = () => {
    if (removeAgentUserTarget) {
      updateMutation.mutate(
        {
          id: removeAgentUserTarget.id,
          request: { agentUser: null },
        },
        {
          onSuccess: () => setRemoveAgentUserTarget(null),
        }
      );
    }
  };

  const isAllSelected =
    agentIdentities &&
    agentIdentities.length > 0 &&
    agentIdentities.every((identity) => selectedIds.has(identity.id));

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <div className="h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Agent Identities</h1>
        <div className="flex items-center gap-3">
          <Link to="/agent-identities/new">
            <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 rounded-lg transition-colors">
              <Plus className="h-4 w-4" />
              Create Agent Identity
            </button>
          </Link>
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="mb-3 flex items-center gap-3 px-4 py-2 bg-gray-50 dark:bg-[#161616] border border-gray-200 dark:border-[#2a2a2a] rounded-lg">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {selectedIds.size} agent identit{selectedIds.size > 1 ? 'ies' : 'y'} selected
          </span>
          <button
            onClick={() => setShowBulkDeleteModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            Delete Selected
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" aria-label="Agent identities">
            <thead className="bg-white dark:bg-[#0a0a0a] border-b border-gray-200 dark:border-[#2a2a2a]">
              <tr>
                <th scope="col" className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-gray-300 dark:border-gray-700 dark:bg-[#161616]"
                    aria-label="Select all agent identities"
                  />
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Display Name
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Tenant ID
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Agent User
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Sponsors
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Created
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-[#0a0a0a] divide-y divide-gray-200 dark:divide-[#2a2a2a]">
              {!agentIdentities || agentIdentities.length === 0 ? (
                <tr className="bg-white dark:bg-[#0a0a0a]">
                  <td colSpan={7} className="px-6 py-32">
                    <div className="flex flex-col items-center justify-center text-center">
                      <UserCog className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
                      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-1">
                        No Agent Identities yet.
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        Create your first agent identity to enable AI agents to authenticate to
                        Azure resources.
                      </p>
                      <Link to="/agent-identities/new">
                        <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 rounded-lg transition-colors">
                          <Plus className="h-4 w-4" />
                          Create Agent Identity
                        </button>
                      </Link>
                    </div>
                  </td>
                </tr>
              ) : (
                agentIdentities.map((identity: AgentIdentityResponse) => (
                  <tr key={identity.id} className="hover:bg-gray-50 dark:hover:bg-[#161616]">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(identity.id)}
                        onChange={(e) => handleSelectOne(identity.id, e.target.checked)}
                        className="rounded border-gray-300 dark:border-gray-700 dark:bg-[#161616]"
                        aria-label={`Select agent identity ${identity.displayName}`}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-200">
                        {identity.displayName}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                        {identity.id}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                        {identity.tenantId.length > 20
                          ? `${identity.tenantId.substring(0, 20)}...`
                          : identity.tenantId}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {identity.agentUser ? (
                        <div>
                          <Badge variant="success">Active</Badge>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {identity.agentUser.userPrincipalName}
                          </div>
                        </div>
                      ) : (
                        <Badge variant="neutral">None</Badge>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {identity.sponsors.length} sponsor
                        {identity.sponsors.length !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {identity.createdAt
                        ? formatDistanceToNow(new Date(identity.createdAt), {
                            addSuffix: true,
                          })
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end items-center gap-2">
                        {identity.agentUser ? (
                          <button
                            onClick={() => setRemoveAgentUserTarget(identity)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-warning-700 dark:text-warning-400 hover:bg-warning-50 dark:hover:bg-warning-900/20 rounded transition-colors"
                            title="Remove Agent User"
                          >
                            <UserMinus className="h-4 w-4" />
                            Remove User
                          </button>
                        ) : (
                          <button
                            onClick={() => setAddAgentUserTarget(identity)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-success-700 dark:text-success-400 hover:bg-success-50 dark:hover:bg-success-900/20 rounded transition-colors"
                            title="Add Agent User"
                          >
                            <UserPlus className="h-4 w-4" />
                            Add User
                          </button>
                        )}
                        <button
                          onClick={() => setDeleteTarget(identity)}
                          className="p-2 text-danger-500 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded transition-colors"
                          aria-label="Delete agent identity"
                          title="Delete agent identity"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Agent Identity"
        message={`Are you sure you want to delete "${deleteTarget?.displayName}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Remove Agent User Confirmation Modal */}
      <ConfirmModal
        isOpen={!!removeAgentUserTarget}
        onClose={() => setRemoveAgentUserTarget(null)}
        onConfirm={handleRemoveAgentUser}
        title="Remove Agent User"
        message={`Are you sure you want to remove the agent user "${removeAgentUserTarget?.agentUser?.userPrincipalName}" from "${removeAgentUserTarget?.displayName}"?`}
        confirmText="Remove"
        variant="danger"
        isLoading={updateMutation.isPending}
      />

      {/* Add Agent User Modal */}
      {addAgentUserTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            className="bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg shadow-xl max-w-md w-full mx-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-agent-user-title"
          >
            <div className="p-6">
              <h2
                id="add-agent-user-title"
                className="text-xl font-semibold text-gray-900 dark:text-white mb-4"
              >
                Add Agent User
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Add an agent user to &quot;{addAgentUserTarget.displayName}&quot;. The user
                principal name must be in a domain owned by the tenant.
              </p>
              <div className="mb-6">
                <label
                  htmlFor="userPrincipalName"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  User Principal Name
                </label>
                <input
                  type="text"
                  id="userPrincipalName"
                  value={userPrincipalName}
                  onChange={(e) => setUserPrincipalName(e.target.value)}
                  placeholder="agent@yourdomain.onmicrosoft.com"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#161616] text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setAddAgentUserTarget(null);
                    setUserPrincipalName('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#161616] border border-gray-300 dark:border-[#2a2a2a] rounded-lg hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddAgentUser}
                  disabled={!userPrincipalName.trim() || updateMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updateMutation.isPending ? 'Adding...' : 'Add Agent User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            className="bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg shadow-xl max-w-md w-full mx-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bulk-delete-title"
          >
            <div className="p-6">
              <h2
                id="bulk-delete-title"
                className="text-xl font-semibold text-gray-900 dark:text-white mb-4"
              >
                Delete {selectedIds.size} Agent Identit{selectedIds.size > 1 ? 'ies' : 'y'}?
              </h2>

              {!isBulkDeleting ? (
                <>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Are you sure you want to delete {selectedIds.size} agent identit
                    {selectedIds.size > 1 ? 'ies' : 'y'}? This action cannot be undone.
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowBulkDeleteModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#161616] border border-gray-300 dark:border-[#2a2a2a] rounded-lg hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                    >
                      Delete All
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <span>Deleting agent identities...</span>
                      <span>
                        {bulkDeleteProgress.current} / {bulkDeleteProgress.total}
                      </span>
                    </div>
                    <div
                      className="w-full bg-gray-200 dark:bg-[#161616] rounded-full h-2.5"
                      role="progressbar"
                      aria-valuenow={bulkDeleteProgress.current}
                      aria-valuemin={0}
                      aria-valuemax={bulkDeleteProgress.total}
                      aria-label="Bulk delete progress"
                    >
                      <div
                        className="bg-red-600 h-2.5 rounded-full transition-all duration-300"
                        style={{
                          width: `${(bulkDeleteProgress.current / bulkDeleteProgress.total) * 100}%`,
                        }}
                      ></div>
                    </div>
                    {bulkDeleteProgress.currentId && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Deleting: {bulkDeleteProgress.currentId}
                      </p>
                    )}
                  </div>
                  {bulkDeleteProgress.current === bulkDeleteProgress.total && (
                    <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                      All agent identities deleted successfully
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
